import { Injectable, Inject, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { Observable, of } from "rxjs";
import { shareReplay, tap } from "rxjs/operators";

interface StorageEntry<T> {
  data: T;
  expiresAt: number;
}

const STORAGE_PREFIX = "siila:cache:";
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 1 day

@Injectable({
  providedIn: "root"
})
export class MemoryCacheService {
  private memCache = new Map<string, Observable<any>>();

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  has(key: string): boolean {
    return this.memCache.has(key) || this.readFromStorage(key) !== null;
  }

  get<T>(key: string): Observable<T> | null {
    // 1. Memory hit (fastest — same tab)
    if (this.memCache.has(key)) {
      return this.memCache.get(key) as Observable<T>;
    }

    // 2. localStorage hit (cross-tab / mobile / page reload)
    const stored = this.readFromStorage<T>(key);
    if (stored !== null) {
      const obs$ = of(stored).pipe(shareReplay(1));
      this.memCache.set(key, obs$);
      return obs$;
    }

    return null;
  }

  set<T>(
    key: string,
    obs$: Observable<T>,
    ttlMs: number = DEFAULT_TTL_MS
  ): Observable<T> {
    const shared$ = obs$.pipe(
      tap(data => this.writeToStorage(key, data, ttlMs)),
      shareReplay(1)
    );
    this.memCache.set(key, shared$);
    return shared$;
  }

  clear(key?: string): void {
    if (key) {
      this.memCache.delete(key);
      this.removeFromStorage(key);
    } else {
      this.memCache.clear();
      this.clearAllStorage();
    }
  }

  private writeToStorage<T>(key: string, data: T, ttlMs: number): void {
    if (!this.isBrowser) return;
    try {
      const entry: StorageEntry<T> = { data, expiresAt: Date.now() + ttlMs };
      localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
    } catch (e) {
      console.warn(
        `[MemoryCacheService] localStorage write failed for "${key}"`,
        e
      );
    }
  }

  private readFromStorage<T>(key: string): T | null {
    if (!this.isBrowser) return null;
    try {
      const raw = localStorage.getItem(STORAGE_PREFIX + key);
      if (!raw) return null;
      const entry: StorageEntry<T> = JSON.parse(raw);
      if (Date.now() > entry.expiresAt) {
        localStorage.removeItem(STORAGE_PREFIX + key);
        return null;
      }
      return entry.data;
    } catch (e) {
      return null;
    }
  }

  private removeFromStorage(key: string): void {
    if (!this.isBrowser) return;
    try {
      localStorage.removeItem(STORAGE_PREFIX + key);
    } catch (e) {}
  }

  private clearAllStorage(): void {
    if (!this.isBrowser) return;
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith(STORAGE_PREFIX))
        .forEach(k => localStorage.removeItem(k));
    } catch (e) {}
  }
}
